import { ReactElement, useState, useEffect } from 'react';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { FIELD_CONFIGS } from '@backrs_ui/grow-ui/src/pages/Profile/configs/field.config';
import { useGetSelectedOptions } from '@backrs_ui/grow-ui/src/pages/Profile/hooks/useGetSelectedOptions';
import { ProfileEditSectionItem } from '@backrs_ui/grow-ui/src/pages/Profile/ProfileEditSectionItem';
import { ProfileFieldConfig } from '@backrs_ui/grow-ui/src/pages/Profile/types/profile.types';
import { filterEditProfileProfileOptions } from '@backrs_ui/grow-ui/src/pages/Profile/utils/options.helpers';
import {
  ProfileFields,
  StoryDocument,
  SchoolApiAutocompleteOption,
  useFetchUserProfileInfoQuery,
  useFetchProfileValueOptionsQuery,
} from '@backrs_ui/grow-ui/src/redux/profile/profile';
import { USER_PROFILE_FIELDS } from '@backrs_ui/grow-ui/src/redux/profile/profile.constants';

import { Box, SxProps, Typography, Button, TextField, Radio } from '@mui/material';
import { Theme } from '@mui/material/styles';

import { ResumeQuestionField } from './questions.config';
import { AnnotatedFormData, AnnotatedFormField, FormValue, FormValues } from './types';

const QUESTION_STRINGS = appStrings.modules.solutionSessions.questions;

export type ResumeQuestionFormProps = Readonly<{
  fields: ResumeQuestionField[];
  sendQAMessage: (message: string, LLMInput?: string) => void;
  onFieldChange?: (fieldKey: string, values: FormValue[]) => void;
  onSubmit?: (formValues: FormValues) => void;
  initialValues?: FormValues;
  formatSubmissionData?: (formValues: FormValues) => FormValues;
  getDefaultValue?: (
    fieldKey: string,
    formValues: FormValues,
  ) => FormValue | FormValue[] | undefined;
}>;

export const ResumeQuestionForm = ({
  fields,
  sendQAMessage,
  onFieldChange,
  onSubmit,
  initialValues = {},
  formatSubmissionData,
  getDefaultValue: customGetDefaultValue,
}: ResumeQuestionFormProps): ReactElement => {
  const [formValues, setFormValues] = useState<FormValues>(initialValues);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const { data: unfilteredProfileOptions } = useFetchProfileValueOptionsQuery(undefined);
  const profileOptions = filterEditProfileProfileOptions(unfilteredProfileOptions);

  const { data: userProfileData } = useFetchUserProfileInfoQuery(USER_PROFILE_FIELDS);
  const { data: profileData, isLoading } = useFetchUserProfileInfoQuery(USER_PROFILE_FIELDS);

  const selectedOptions = useGetSelectedOptions({
    userProfile: profileData || userProfileData,
    isEditView: true,
  });

  useEffect(() => {
    setFormValues(initialValues);
  }, [initialValues]);

  const setFieldError = (fieldKey: string, error: string) => {
    setFieldErrors((prev) => ({
      ...prev,
      [fieldKey]: error,
    }));
  };

  const clearFieldError = (fieldKey: string) => {
    setFieldErrors((prev) => {
      return Object.fromEntries(Object.entries(prev).filter(([key]) => key !== fieldKey));
    });
  };

  const getDefaultValue = (fieldKey: string): FormValue | FormValue[] | undefined => {
    if (customGetDefaultValue) {
      return customGetDefaultValue(fieldKey, formValues);
    }

    return formValues[fieldKey];
  };

  const getFieldConfig = (fieldKey: ProfileFields, maxSelectedOptionsOverride?: number) => {
    const config = FIELD_CONFIGS[fieldKey] as ProfileFieldConfig;

    const options = profileOptions?.[fieldKey];

    const defaultValue = getDefaultValue(fieldKey) as ProfileFieldConfig['defaultValue'];
    const fieldConfig = fields.find((field) => field.key === fieldKey);
    const isRequired = fieldConfig?.required;

    const maxSelectedOptions = maxSelectedOptionsOverride ?? config?.maxSelectedOptions;

    return {
      ...config,
      options,
      defaultValue,
      maxSelectedOptions,
      disabledBy: undefined,
      storyDocuments: selectedOptions(fieldKey) as StoryDocument[],
      rules:
        isRequired && hasSubmitted ? { required: QUESTION_STRINGS.requiredFieldError } : undefined,
      onChange: (values: FormValue[]) => {
        setFormValues((prev) => {
          const updatedValues = { ...prev, [fieldKey]: values as FormValue[] };

          // for institution enrollement fields, we autofill the city and state if we have location info
          if (
            (fieldKey === 'academicsHighSchoolCurrentEnrollment' ||
              fieldKey === 'academicsPostSecondaryCurrentEnrollment') &&
            values.length > 0
          ) {
            const schoolValue = values[0] as SchoolApiAutocompleteOption;

            if (schoolValue?.city) {
              updatedValues.institution_city = [schoolValue.city];
            }

            if (schoolValue?.state) {
              updatedValues.institution_state = [schoolValue.state];
            }
          }

          return updatedValues;
        });

        if (onFieldChange) {
          onFieldChange(fieldKey, values as FormValue[]);
        }
      },
    };
  };

  const handleCustomFieldChange = (fieldKey: string, value: string) => {
    setFormValues((prev) => ({ ...prev, [fieldKey]: [value] }));

    const fieldConfig = fields.find((field) => field.key === fieldKey);
    const validationError = fieldConfig?.validation ? fieldConfig.validation(value) : '';

    if (validationError) {
      setFieldError(fieldKey, validationError);
    } else {
      clearFieldError(fieldKey);
      if (onFieldChange) {
        onFieldChange(fieldKey, [value]);
      }
    }
  };

  const handleSubmit = async () => {
    setHasSubmitted(true);

    const hasRequiredFieldErrors = fields.some(
      (field) => field.required && (!formValues[field.key] || formValues[field.key].length === 0),
    );

    const hasValidationErrors = Object.keys(fieldErrors).length > 0;

    if (hasRequiredFieldErrors || hasValidationErrors) return;

    setFieldErrors({});

    const submissionData = formatSubmissionData ? formatSubmissionData(formValues) : formValues;

    const fieldsByKey = new Map(fields.map((field) => [field.key, field]));
    const annotatedFormData: AnnotatedFormData = Object.fromEntries(
      Object.entries(submissionData).map(([key, value]): [string, AnnotatedFormField] => {
        const field = fieldsByKey.get(key);
        const label = field?.label ?? field?.getLabel?.(formValues);

        return [key, { label, required: Boolean(field?.required), value }];
      }),
    );

    if (onSubmit) {
      await onSubmit(formValues);
    }

    sendQAMessage(`<FORM_DATA>${JSON.stringify(annotatedFormData)}</FORM_DATA>`, 'Form submitted.');
  };

  const handleSkip = () => {
    sendQAMessage('DONE', "That's it!");
  };

  const renderCustomField = (field: ResumeQuestionField): ReactElement => {
    const fieldKey = field.key;
    const currentValue = formValues[fieldKey]?.[0] || '';

    switch (field.type) {
      case 'small_freeform':
        return (
          <TextField
            id={`resume-field-${fieldKey}`}
            size="small"
            value={currentValue}
            onChange={(e) => {
              const value = e.target.value;
              const formattedValue = field.formatValue ? field.formatValue(value) : value;

              if (field.validation) {
                const validationError = field.validation(formattedValue);

                if (validationError) {
                  setFieldError(fieldKey, validationError);
                } else {
                  clearFieldError(fieldKey);
                }
              }

              handleCustomFieldChange(fieldKey, formattedValue);
            }}
            placeholder={field.placeholder}
            fullWidth
            error={(field.required && hasSubmitted && !currentValue) || !!fieldErrors[fieldKey]}
            helperText={
              field.required && hasSubmitted && !currentValue
                ? QUESTION_STRINGS.requiredFieldError
                : fieldErrors[fieldKey] || ''
            }
          />
        );
      case 'yes_no':
        return (
          <>
            <Box sx={yesNoContainerSx}>
              <Box sx={radioGroupSx}>
                <Radio
                  id="resume-radio-yes"
                  value="Yes"
                  onClick={() => handleCustomFieldChange(fieldKey, 'Yes')}
                  sx={radioSx}
                  checked={currentValue === 'Yes'}
                />
                <Typography variant="text-sm-semibold">Yes</Typography>
              </Box>
              <Box sx={radioGroupSx}>
                <Radio
                  id="resume-radio-no"
                  value="No"
                  onClick={() => handleCustomFieldChange(fieldKey, 'No')}
                  sx={radioSx}
                  checked={currentValue === 'No'}
                />
                <Typography variant="text-sm-semibold">No</Typography>
              </Box>
            </Box>
            {field.required && hasSubmitted && !currentValue && (
              <Typography
                variant="text-sm-regular"
                color={({ backrs }) => backrs.colors['Colors/Text/text-error-primary']}
              >
                {QUESTION_STRINGS.requiredFieldError}
              </Typography>
            )}
          </>
        );
      case 'large_freeform':
        return (
          <TextField
            id={`resume-textarea-${fieldKey}`}
            multiline
            rows={4}
            value={currentValue}
            onChange={(e) => {
              const value = e.target.value;

              if (field.validation) {
                const validationError = field.validation(value);

                if (validationError) {
                  setFieldError(fieldKey, validationError);
                } else {
                  clearFieldError(fieldKey);
                }
              }

              handleCustomFieldChange(fieldKey, value);
            }}
            placeholder={field.placeholder}
            fullWidth
            error={(field.required && hasSubmitted && !currentValue) || !!fieldErrors[fieldKey]}
            helperText={
              field.required && hasSubmitted && !currentValue
                ? QUESTION_STRINGS.requiredFieldError
                : fieldErrors[fieldKey] || ''
            }
          />
        );
      case 'mm/yyyy':
        return (
          <TextField
            id={`resume-input-${fieldKey}`}
            size="small"
            value={currentValue}
            onChange={(e) => {
              const input = e.target.value.replace(/\D/g, '');
              const formattedValue = field.formatValue ? field.formatValue(input) : input;

              if (field.validation) {
                const validationError = field.validation(formattedValue);

                if (validationError) {
                  setFieldError(fieldKey, validationError);
                } else {
                  clearFieldError(fieldKey);
                }
              }

              handleCustomFieldChange(fieldKey, formattedValue);
            }}
            placeholder={field.placeholder}
            fullWidth
            error={(field.required && hasSubmitted && !currentValue) || !!fieldErrors[fieldKey]}
            helperText={
              field.required && hasSubmitted && !currentValue
                ? QUESTION_STRINGS.requiredFieldError
                : fieldErrors[fieldKey] || field.helperText || ''
            }
            inputProps={{
              maxLength: 7,
            }}
          />
        );
      default:
        return <></>;
    }
  };

  const getFieldLabel = (field: ResumeQuestionField): string => {
    const label = field.getLabel ? field.getLabel(formValues) : field.label || '';

    return field.required ? `${label}*` : label;
  };

  if (!profileOptions || isLoading) {
    return <></>;
  }

  return (
    <Box sx={editSectionContainerSx}>
      <Box sx={containerSx}>
        <Box sx={subContainerSx}>
          {fields.map((field) => {
            const fieldConfig =
              field.type === 'profile'
                ? getFieldConfig(field.key as ProfileFields, field.maxSelectedOptions)
                : null;

            return (
              <Box key={field.key} sx={fieldContainerSx}>
                <Typography variant="text-sm-semibold">{getFieldLabel(field)}</Typography>
                {field.type === 'profile' && fieldConfig ? (
                  <ProfileEditSectionItem
                    field={fieldConfig}
                    options={Array.isArray(fieldConfig.options) ? fieldConfig.options : []}
                    error={
                      field.required &&
                      hasSubmitted &&
                      (!formValues[field.key] || formValues[field.key].length === 0)
                        ? QUESTION_STRINGS.requiredFieldError
                        : undefined
                    }
                  />
                ) : field.type ? (
                  renderCustomField(field)
                ) : null}
              </Box>
            );
          })}
        </Box>
        <Box sx={submitButtonContainerSx}>
          <Button id="resume-skip-button" hierarchy="Secondary gray" size="md" onClick={handleSkip}>
            {QUESTION_STRINGS.skip}
          </Button>
          <Button
            id="resume-submit-button"
            hierarchy="Primary"
            size="md"
            onClick={handleSubmit}
            disabled={Object.keys(formValues).length === 0 || Object.keys(fieldErrors).length > 0}
          >
            {QUESTION_STRINGS.submit}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

const containerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
};

const subContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: backrs.colors['Colors/Base/White'],
  borderRadius: backrs.radius['radius-md'],
  gap: backrs.spacing['spacing-lg'],
  px: backrs.spacing['spacing-4xl'],
  py: backrs.spacing['spacing-2xl'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  width: '100%',
});

const fieldContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
  width: '100%',
});

const editSectionContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-2xl'],
  position: 'relative',
  zIndex: 1,
});

const submitButtonContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  mt: backrs.spacing['spacing-xl'],
  gap: backrs.spacing['spacing-xl'],
});

const radioSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Colors/Base/White'],
  color: backrs.colors['Colors/Base/Black'],
  margin: 0,
});

const yesNoContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  gap: backrs.spacing['spacing-2xl'],
});

const radioGroupSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
});
